import { ref, computed, onUnmounted } from 'vue'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { auth, db } from '@/firebase/config'
import { USER_ROLES, hasPermission } from '@/utils/roles'

const user = ref(null)
const isAuthenticated = computed(() => !!user.value)
const userRole = computed(() => user.value?.role || null)
const isLoading = ref(true)
const error = ref(null)

// Store the unsubscribe function
let authUnsubscribe = null

export function useFirebaseAuth() {
  // Auto cleanup when component unmounts
  onUnmounted(() => {
    if (authUnsubscribe) {
      authUnsubscribe()
      authUnsubscribe = null
    }
  })
  const login = async (email, password) => {
    error.value = null
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid))
      
      if (userDoc.exists()) {
        user.value = {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          ...userDoc.data()
        }
      } else {
        user.value = {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName
        }
      }
      
      return { success: true, user: user.value }
    } catch (err) {
      let errorMessage = 'Login failed. Please try again.'
      
      // Provide specific error messages based on Firebase error codes
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address. Please check your email or create a new account.'
          break
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please check your password and try again.'
          break
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format. Please enter a valid email.'
          break
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.'
          break
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed login attempts. Please try again later.'
          break
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection and try again.'
          break
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password. Please check your credentials and try again.'
          break
        default:
          errorMessage = err.message || 'Login failed. Please try again.'
      }
      
      error.value = errorMessage
      return { success: false, error: errorMessage }
    }
  }

  const register = async (email, password, displayName = '', role = USER_ROLES.USER) => {
    error.value = null
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      
      if (displayName) {
        await updateProfile(userCredential.user, { displayName })
      }
      
      const userData = {
        email: userCredential.user.email,
        displayName: displayName || email.split('@')[0],
        role: role,
        status: role === USER_ROLES.DOCTOR ? 'pending_verification' : 'active',
        createdAt: new Date().toISOString(),
        profile: {
          bio: '',
          avatar: '',
          phone: '',
          dateOfBirth: '',
          address: '',
          // Doctor-specific fields
          ...(role === USER_ROLES.DOCTOR && {
            specialization: '',
            licenseNumber: '',
            yearsOfExperience: 0,
            verified: false
          })
        }
      }
      
      await setDoc(doc(db, 'users', userCredential.user.uid), userData)
      
      user.value = {
        uid: userCredential.user.uid,
        ...userData
      }
      
      return { success: true, user: user.value }
    } catch (err) {
      let errorMessage = 'Registration failed. Please try again.'
      
      // Provide specific error messages based on Firebase error codes
      switch (err.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email address already exists. Please use a different email or try logging in.'
          break
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format. Please enter a valid email.'
          break
        case 'auth/weak-password':
          errorMessage = 'Password is too weak. Please choose a stronger password with at least 6 characters.'
          break
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password accounts are not enabled. Please contact support.'
          break
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection and try again.'
          break
        default:
          errorMessage = err.message || 'Registration failed. Please try again.'
      }
      
      error.value = errorMessage
      return { success: false, error: errorMessage }
    }
  }

  const loginWithGoogle = async () => {
    error.value = null
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      const firebaseUser = result.user

      // Check if user document exists in Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid)
      const userDoc = await getDoc(userDocRef)

      if (!userDoc.exists()) {
        // Create new user document for first-time Google login
        const userData = {
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          role: USER_ROLES.USER, // All Google login users are regular users
          status: 'active',
          createdAt: new Date().toISOString(),
          profile: {
            bio: '',
            avatar: firebaseUser.photoURL || '',
            phone: '',
            dateOfBirth: '',
            address: ''
          },
          authProvider: 'google'
        }

        await setDoc(userDocRef, userData)

        user.value = {
          uid: firebaseUser.uid,
          ...userData
        }
      } else {
        // Existing user, load their data
        user.value = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          ...userDoc.data()
        }
      }

      return { success: true, user: user.value }
    } catch (err) {
      let errorMessage = 'Google login failed. Please try again.'

      switch (err.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Login cancelled. Please try again.'
          break
        case 'auth/popup-blocked':
          errorMessage = 'Popup blocked. Please allow popups for this site.'
          break
        case 'auth/cancelled-popup-request':
          errorMessage = 'Login cancelled. Please try again.'
          break
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection.'
          break
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different login method.'
          break
        default:
          errorMessage = err.message || 'Google login failed. Please try again.'
      }

      error.value = errorMessage
      return { success: false, error: errorMessage }
    }
  }

  const logout = async () => {
    try {
      await signOut(auth)
      user.value = null
      return { success: true }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    }
  }

  const initAuth = () => {
    // Cleanup previous listener if exists
    if (authUnsubscribe) {
      authUnsubscribe()
    }
    
    authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        isLoading.value = true
        
        if (firebaseUser) {
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
            
            if (userDoc.exists()) {
              user.value = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                ...userDoc.data()
              }
            } else {
              user.value = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName
              }
            }
          } catch (err) {
            console.error('Error fetching user data:', err)
            user.value = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName
            }
          }
        } else {
          user.value = null
        }
      } catch (err) {
        console.error('Auth state change error:', err)
      } finally {
        isLoading.value = false
      }
    })
  }

  const cleanup = () => {
    if (authUnsubscribe) {
      authUnsubscribe()
      authUnsubscribe = null
    }
  }

  const updateUserProfile = async (updates) => {
    if (!user.value) return { success: false, error: 'No user logged in' }
    
    try {
      await setDoc(doc(db, 'users', user.value.uid), updates, { merge: true })
      
      user.value = {
        ...user.value,
        ...updates
      }
      
      return { success: true, user: user.value }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    }
  }

  // Role checking functions
  const isAdmin = computed(() => userRole.value === USER_ROLES.ADMIN)
  const isDoctor = computed(() => userRole.value === USER_ROLES.DOCTOR)
  const isUser = computed(() => userRole.value === USER_ROLES.USER)
  
  const checkPermission = (permission) => {
    return hasPermission(userRole.value, permission)
  }

  return {
    user: computed(() => user.value),
    userRole,
    isAuthenticated,
    isAdmin,
    isDoctor,
    isUser,
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    login,
    register,
    loginWithGoogle,
    logout,
    initAuth,
    cleanup,
    updateUserProfile,
    checkPermission
  }
}